import { useState } from 'react';
import Reveal from '@/components/base/Reveal';

const presets = [2000, 5000, 10000, 25000];

function formatCurrency(n: number) {
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

export default function RoiCalculator() {
  const [spend, setSpend] = useState(5000);

  const revenue = spend * 12;
  const growth = revenue - spend;

  return (
    <section id="roi-calculator" className="relative py-20 md:py-28">
      {/* glow */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="w-[560px] h-[560px] rounded-full bg-primary-500/8 blur-[130px]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 md:px-6">
        <Reveal className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-500/30 bg-primary-500/10 px-4 py-1.5 text-xs font-label font-semibold text-primary-400">
            <i className="ri-calculator-line"></i>
            ROI Calculator
          </span>
          <h2 className="mt-6 font-heading font-bold text-3xl md:text-4xl text-foreground-950">
            See What Your Marketing Could Return
          </h2>
          <p className="mt-4 text-foreground-600">
            Enter your monthly marketing and ad spend to estimate the potential revenue and growth
            opportunity based on historical performance.
          </p>
        </Reveal>

        <Reveal delay={100}>
          <div className="mt-12 rounded-2xl border border-white/10 bg-background-100 p-6 md:p-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              {/* Input */}
              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor="spend" className="font-label font-semibold text-sm text-foreground-700">
                    Monthly Marketing &amp; Ad Spend
                  </label>
                  <span className="font-heading font-bold text-xl text-primary-400">
                    {formatCurrency(spend)}
                  </span>
                </div>

                <input
                  id="spend"
                  type="range"
                  min={500}
                  max={50000}
                  step={500}
                  value={spend}
                  onChange={(e) => setSpend(Number(e.target.value))}
                  className="mt-6 w-full h-2 appearance-none rounded-full bg-background-300 cursor-pointer accent-primary-500"
                  aria-label="Monthly marketing and ad spend"
                />

                <div className="mt-2 flex justify-between text-xs text-foreground-600">
                  <span>$500</span>
                  <span>$50,000</span>
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  {presets.map((p) => (
                    <button
                      key={p}
                      onClick={() => setSpend(p)}
                      className={`px-4 py-2 rounded-md text-xs font-label font-semibold transition-colors whitespace-nowrap ${
                        spend === p
                          ? 'bg-primary-500 text-background-950'
                          : 'bg-background-200 text-foreground-700 hover:text-primary-400'
                      }`}
                    >
                      ${(p / 1000).toFixed(0)}K
                    </button>
                  ))}
                </div>
              </div>

              {/* Outputs */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-xl border border-white/8 bg-background-200/60 p-5">
                  <p className="text-xs text-foreground-600">Estimated Revenue</p>
                  <p className="mt-2 font-heading font-extrabold text-2xl md:text-3xl text-primary-400">
                    {formatCurrency(revenue)}
                  </p>
                </div>
                <div className="rounded-xl border border-white/8 bg-background-200/60 p-5">
                  <p className="text-xs text-foreground-600">Potential Return</p>
                  <p className="mt-2 font-heading font-extrabold text-2xl md:text-3xl text-accent-400">
                    12x
                  </p>
                </div>
                <div className="rounded-xl border border-white/8 bg-background-200/60 p-5">
                  <p className="text-xs text-foreground-600">Growth Opportunity</p>
                  <p className="mt-2 font-heading font-extrabold text-2xl md:text-3xl text-accent-400">
                    +{formatCurrency(growth)}
                  </p>
                </div>
              </div>
            </div>

            <p className="mt-8 text-xs text-foreground-600">
              Based on historical campaign performance. Results vary by business, market, offer, and
              execution.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}